import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import makeStyles from '@mui/styles/makeStyles';
import Box from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import Sidebar from 'components/Sidebar';
import Session from 'utils/Session';
import { signOut } from 'services/auth';
import { getIELTSPlan } from 'utils/ieltsAccess';

const PLAN_META = {
    unlimited:    { label: "Premium Plan",  color: "#7C3AED", bg: "#F5F3FF" },
    item_unlocked:{ label: "Standard Plan", color: "#0284C5", bg: "#E0F2FE" },
    free:         { label: "Free Plan",     color: "#16A34A", bg: "#DCFCE7" },
    basic:        { label: "Free Plan",     color: "#16A34A", bg: "#DCFCE7" },
};

const NAV_LINKS = [
    { label: 'Free Study Material', path: '/free-study-material', authRequired: true },
    { label: 'About', path: '/about' },
    { label: 'IELTS Practice', path: '/practice' },
    { label: 'Blog', path: 'https://blog.languify.in/?p=942', external: true },
];

export const MenuItems = NAV_LINKS.map(item => ({ title: item.label, link: item.path }));

const useStyles = makeStyles(theme => ({
    header: {
        width: '100%',
        backgroundColor: theme.palette.shades['clr-white-900'],
        borderBottom: `1px solid ${theme.palette.neutral['clr-200']}`,
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 40px',
        [theme.breakpoints.down('md')]: {
            padding: '0 16px',
        },
    },
    logoLink: {
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'baseline',
        gap: '6px',
    },
    rightSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '0px',
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
    navLinks: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    authButtons: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
    },
    loginButton: {
        ...theme.typography['body01-medium'],
        textTransform: 'none',
        color: theme.palette.neutral['clr-700'],
        minWidth: 'unset',
        padding: '6px 12px',
        '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.neutral['clr-900'],
        },
    },
    signUpButton: {
        ...theme.typography['body02-semiBold'],
        backgroundColor: '#0284C5',
        color: theme.palette.shades['clr-white-900'],
        borderRadius: '4px',
        padding: '6px 20px',
        textTransform: 'none',
        minWidth: 'unset',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#026BA1',
            boxShadow: 'none',
        },
    },
    mobileElement: {
        display: 'none',
        cursor: 'pointer',
        [theme.breakpoints.down('md')]: {
            display: 'block',
        },
    },
}));

const NavLink = ({ label, path, active, authRequired, external }) => {
    const navigate = useNavigate();

    const handleClick = (e) => {
        if (authRequired && !Session.isLoggedIn()) {
            e.preventDefault();
            navigate('/register');
        }
    };

    if (active) {
        return (
            <MUIButton
                component={external ? 'a' : Link}
                {...(external ? { href: path, target: '_blank', rel: 'noreferrer' } : { to: path })}
                onClick={handleClick}
                disableElevation
                sx={(theme) => ({
                    ...theme.typography['body02-semiBold'],
                    backgroundColor: '#0284C5',
                    color: '#FFFFFF',
                    borderRadius: '4px',
                    padding: '6px 14px',
                    textTransform: 'none',
                    minWidth: 'unset',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#026BA1',
                        boxShadow: 'none',
                    },
                })}
            >
                {label}
            </MUIButton>
        );
    }

    return (
        <MUIButton
            component={external ? 'a' : Link}
            {...(external ? { href: path, target: '_blank', rel: 'noreferrer' } : { to: path })}
            onClick={handleClick}
            sx={(theme) => ({
                ...theme.typography['body01-medium'],
                textTransform: 'none',
                color: theme.palette.neutral['clr-700'],
                fontWeight: 500,
                minWidth: 'unset',
                padding: '6px 14px',
                '&:hover': {
                    backgroundColor: 'transparent',
                    color: theme.palette.neutral['clr-900'],
                },
            })}
        >
            {label}
        </MUIButton>
    );
};

function ProfileMenu({ onLogout }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const { planType } = getIELTSPlan();
    const meta = PLAN_META[planType] || null;
    const expiry = Session.user?.metadata?.expiry;
    const formattedExpiry = expiry
        ? new Date(expiry).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
        : "Lifetime";
    const email = Session.user?.email || "";
    const initial = email.charAt(0).toUpperCase();

    return (
        <>
            <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    backgroundColor: '#0284C5',
                    color: '#FFFFFF',
                    width: 32,
                    height: 32,
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 700,
                    '&:hover': { backgroundColor: '#026BA1' },
                }}
            >
                {initial}
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                    sx: {
                        mt: '8px',
                        minWidth: 240,
                        borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        overflow: 'visible',
                        py: '4px',
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Plan info */}
                {meta && (
                    <Box sx={{ px: '16px', pt: '12px', pb: '10px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Box
                                sx={{
                                    width: 34, height: 34, borderRadius: '8px',
                                    backgroundColor: meta.bg,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: meta.color }} />
                            </Box>
                            <Box>
                                <Typography sx={{
                                    fontFamily: 'Mulish, sans-serif', fontWeight: 700,
                                    fontSize: '0.88rem', color: '#111827', lineHeight: 1.3,
                                }}>
                                    {meta.label}
                                </Typography>
                                <Typography sx={{
                                    fontFamily: 'Mulish, sans-serif', fontSize: '0.75rem', color: '#9CA3AF',
                                }}>
                                    Valid until {formattedExpiry}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                <Divider sx={{ my: '4px' }} />

                {/* Email */}
                <Box sx={{ px: '16px', py: '10px' }}>
                    <Typography sx={{
                        fontFamily: 'Mulish, sans-serif', fontSize: '0.72rem',
                        color: '#9CA3AF', mb: '2px', textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                        Signed in as
                    </Typography>
                    <Typography sx={{
                        fontFamily: 'Mulish, sans-serif', fontSize: '0.85rem',
                        fontWeight: 600, color: '#374151', wordBreak: 'break-all',
                    }}>
                        {email}
                    </Typography>
                </Box>

                <Divider sx={{ my: '4px' }} />

                {/* Logout */}
                <MenuItem
                    onClick={() => { setAnchorEl(null); onLogout(); }}
                    sx={{
                        px: '16px', py: '10px',
                        fontFamily: 'Mulish, sans-serif',
                        fontSize: '0.88rem',
                        fontWeight: 600,
                        color: '#EF4444',
                        gap: '8px',
                        '&:hover': { backgroundColor: '#FEF2F2' },
                    }}
                >
                    <LogoutIcon sx={{ fontSize: '1rem' }} />
                    Logout
                </MenuItem>
            </Menu>
        </>
    );
}

function Header() {
    const classes = useStyles();
    const location = useLocation();
    const navigate = useNavigate();

    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleOpenSidebar = () => setSidebarOpen(true);

    const handleLogout = async () => {
        await signOut();
        navigate('/');
    };

    const isActive = (item) => {
        if (item.external) return false;
        if (item.path === '/practice') return location.pathname.startsWith('/practice');
        return location.pathname === item.path;
    };

    return (
        <Box className={classes.header}>

            {/* Logo */}
            <Link to="/" className={classes.logoLink}>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography component="span" variant="h6-bold" sx={{ color: '#0284C5' }}>
                        IELTS
                    </Typography>
                    &nbsp;
                    <Typography component="span" variant="h6-bold" color="neutral.clr-900">
                        BandBoost
                    </Typography>
                </Box>
                <Typography component="span" variant="body02-regular" color="neutral.clr-400">
                    by
                </Typography>
                <Typography component="span" variant="body01-medium" color="neutral.clr-700" sx={{ fontStyle: 'italic' }}>
                    languify
                </Typography>
            </Link>

            {/* Nav Links + Auth (right-aligned) */}
            <Box className={classes.rightSection}>
                <Box className={classes.navLinks}>
                    {NAV_LINKS.map((item) => (
                        <NavLink
                            key={item.path}
                            label={item.label}
                            path={item.path}
                            active={isActive(item)}
                            authRequired={item.authRequired}
                            external={item.external}
                        />
                    ))}
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: '16px', borderColor: 'neutral.clr-200' }} />

                <Box className={classes.authButtons}>
                    {Session.isLoggedIn() ? (
                        <ProfileMenu onLogout={handleLogout} />
                    ) : (
                        <>
                            <MUIButton
                                component={Link}
                                to="/login"
                                disableElevation
                                disableRipple
                                className={classes.loginButton}
                            >
                                Login
                            </MUIButton>
                            <MUIButton
                                component={Link}
                                to="/register"
                                variant="contained"
                                disableElevation
                                className={classes.signUpButton}
                            >
                                Sign Up
                            </MUIButton>
                        </>
                    )}
                </Box>
            </Box>

            {/* Mobile Menu */}
            {!sidebarOpen && (
                <MenuIcon
                    className={classes.mobileElement}
                    sx={{ fontSize: '1.8rem', color: 'neutral.clr-700' }}
                    onClick={handleOpenSidebar}
                />
            )}
            {sidebarOpen && <Sidebar setSidebarOpen={setSidebarOpen} />}

        </Box>
    );
}

export default Header;
